/**
 * Conteo de pantallas operativas para el REPARTO de costos compartidos del ER v2.
 *
 * Hoy lo usa `contenidos_digitales` (Screenfeed / AccuWeather / ApiFootball): un servicio que
 * se consume en las pantallas de los DOS países pero cuyas facturas están todas en Chile. El
 * costo se reparte entre Publicidad (Bloque A) y Cartelería (Bloque C) en proporción a las
 * pantallas de cada circuito, sumando Chile + Perú en ambos.
 *
 * Las dos fuentes son las de la regla 10.113 — el inventario físico NO vive en una sola tabla:
 *   · Publicidad → `edificios` (ascensores + tótems + espera + co-work)
 *   · Cartelería → `negocios_carteleria` (`tipo_vertical='EO'`)
 *
 * NO se reusa `contarPantallasActivas` (`utils/proveedoresRecurrentes.js`), que cuenta lo mismo
 * para facturar las licencias de OnSign: es async (query propia), y difiere de la regla 10.113
 * en cinco ejes — no filtra `eliminada` en edificios, no aplica EDIFICIOS_EXCLUIDOS, no filtra
 * `tipo_vertical` (cuela cartelería TD), admite `estado='100%'` (que es pipeline, no instalado)
 * y pierde los operativos con `inicio_servicio` NULL (`NULL <= fecha` es NULL en Postgres).
 * Acá se cuenta sobre los datos que el ER ya tiene cargados, con el predicado del cotizador.
 *
 * SNAPSHOT, no serie mensual: ambos conteos son del estado ACTUAL (`estado='OPERATIVO'` /
 * `'Activo'`), sin eje de tiempo — igual que `getEdificiosOperativos`, que es la definición
 * que pidió el spec. El mismo ratio se aplica a los 12 meses. La identidad A + C = costo se
 * cumple igual, sea el ratio constante o no.
 */
import { generaComision } from "../../constants/estadosEdificio";
import { EDIFICIOS_EXCLUIDOS } from "../../constants/permisos";
import { calendarioNegocio } from "../../utils/carteleria/calendarioNegocio";

const num = (v) => Number(v) || 0;

/**
 * Pantallas del circuito PUBLICIDAD de un país. Mismo predicado que
 * `getEdificiosOperativos` (`generaComision` + vivo + no excluido), aplicado sobre los
 * edificios que el hook ya trajo en vez de re-fetchear: mismo resultado, sin ida a la red
 * (y sin el `toast.error` que ese helper dispara cuando el país no tiene operativos).
 *
 * @param {object|Array} edificios - Mapa por id (shape de useERv2Data) o array de filas
 */
export const contarPantallasPublicidad = (edificios) => {
  const filas = Array.isArray(edificios) ? edificios : Object.values(edificios || {});
  return filas.reduce((total, e) => {
    if (!e) return total;
    if (!generaComision(e.estado) || e.eliminada || EDIFICIOS_EXCLUIDOS.includes(e.id)) return total;
    // Las 4 columnas son circuitos DISTINTOS y se suman (regla 10.113): `pantallas` son los
    // ascensores, no el total del edificio.
    return total + num(e.pantallas) + num(e.totems) + num(e.espera) + num(e.cowork);
  }, 0);
};

/**
 * Pantallas del circuito CARTELERÍA de un país. Solo `tipo_vertical='EO'` (los `OT` son
 * hardware suelto / reemplazos, no inventario de pantalla) y solo instaladas: `estado`
 * 'Activo' — los 50/75/90/100% son PIPELINE (regla 10.113). El casing de `estado` es
 * inconsistente en DB (ver 9.1.19), así que se compara en minúsculas.
 */
export const contarPantallasCarteleria = (negocios) => {
  // F3d: las pantallas, el tipo y el estado se leen del motor `calendarioNegocio`. Es un SNAPSHOT (sin eje de
  // tiempo), así que no hay corte por mes: conserva EXACTO el resultado anterior (EO + estado 'activo' sin
  // distinguir mayúsculas; `cantidad_pantallas` nula = 0, aunque el motor cuente 1 si falta). Referencia congelada:
  // `utils/carteleria/historico/pantallasOperativasCarteleriaHistorico.js`.
  const hoy = new Date();
  return (negocios || []).reduce((total, n) => {
    if (!n || n.eliminada) return total;
    const cal = calendarioNegocio(n, { hoy });
    if (cal.tipoVertical !== "EO") return total;
    if (String(cal.estado || "").toLowerCase() !== "activo") return total;
    return total + (num(n.cantidad_pantallas) ? cal.pantallas : 0);
  }, 0);
};

/**
 * Reparto proporcional PURO de un costo entre Publicidad y Cartelería.
 *
 * A + C === costo por construcción: C se calcula como el resto (`costo − A`), no con su
 * propio producto, así que ningún redondeo puede abrir una brecha entre la suma de las dos
 * filas y el total facturado. Es la diferencia deliberada con el split de Software DS, que
 * es RESIDUAL (`A = max(0, factura − estimado)`) y donde A + C = max(factura, estimado) ≠
 * factura.
 *
 * Sin pantallas (denominador 0) NO se inventa un reparto: todo va a Publicidad. Es el caso
 * degenerado de un país sin inventario cargado; devolver 0/0 escondería el costo entero.
 *
 * @returns {{ publicidad: number, carteleria: number }}
 */
export const repartirPorPantallas = (costo, pantallasPublicidad, pantallasCarteleria) => {
  const monto = num(costo);
  if (!monto) return { publicidad: 0, carteleria: 0 };
  const denominador = num(pantallasPublicidad) + num(pantallasCarteleria);
  if (denominador <= 0) return { publicidad: monto, carteleria: 0 };
  const publicidad = Math.round((monto * num(pantallasPublicidad)) / denominador);
  return { publicidad, carteleria: monto - publicidad };
};
